import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { sendFeedbackData } from '../utils/webhookService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLogOut, FiSettings, FiHome, FiMessageSquare, FiHelpCircle, FiStar, FiCalendar, FiBook, FiTrendingUp } = FiIcons;

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHelpHub, setShowHelpHub] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);

  useEffect(() => {
    // Load onboarding data
    const data = localStorage.getItem('onboardingData');
    if (data) {
      setOnboardingData(JSON.parse(data));
    }
  }, []);

  const handleLogout = () => {
    logout();
  };

  const FeedbackModal = () => {
    const [rating, setRating] = useState(5); // Fixed at 5 stars
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmitFeedback = async () => {
      setIsSubmitting(true);
      
      try {
        const feedbackData = {
          userId: user?.userId,
          rating: rating,
          comment: comment,
          submissionDate: new Date().toISOString(),
          userInfo: onboardingData ? {
            company: onboardingData.company,
            businessType: onboardingData.businessType,
            email: onboardingData.email
          } : null,
          source: 'dashboard-feedback'
        };

        // Send to webhook
        const result = await sendFeedbackData(feedbackData);
        
        if (result.success) {
          alert('Vielen Dank für Ihr Feedback!');
          setShowFeedback(false);
        } else {
          alert('Fehler beim Senden des Feedbacks. Bitte versuchen Sie es erneut.');
        }
      } catch (error) {
        console.error('Feedback submission error:', error);
        alert('Fehler beim Senden des Feedbacks. Bitte versuchen Sie es erneut.');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowFeedback(false)}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl p-8 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center mb-6">
            <SafeIcon icon={FiStar} className="text-4xl text-azure-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-black mb-2">Feedback geben</h3>
            <p className="text-gray-600">Wie war Ihre Erfahrung mit K³?</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bewertung
              </label>
              <div className="flex justify-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <SafeIcon
                    key={star}
                    icon={FiStar}
                    className="text-2xl text-yellow-400 fill-current"
                  />
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">5 von 5 Sternen</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ihr Feedback
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                rows="4"
                placeholder="Teilen Sie Ihre Erfahrungen mit uns..."
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowFeedback(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSubmitFeedback}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Wird gesendet...' : 'Senden'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  const HelpHubModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={() => setShowHelpHub(false)}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-8">
          <SafeIcon icon={FiHelpCircle} className="text-4xl text-azure-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-black mb-2">HelpHub - Häufige Fragen</h3>
          <p className="text-gray-600">Hier finden Sie Antworten auf die wichtigsten Fragen</p>
        </div>
        
        <div className="space-y-6">
          <div className="border-b border-gray-200 pb-4">
            <h4 className="font-semibold text-black mb-2">Wie funktioniert die KI-Automatisierung?</h4>
            <p className="text-gray-600 text-sm">
              Unsere KI-Lösungen automatisieren Ihre Kundenkommunikation durch intelligente Chatbots und Sprachassistenten. 
              Diese können Termine buchen, Fragen beantworten und Leads qualifizieren.
            </p>
          </div>
          
          <div className="border-b border-gray-200 pb-4">
            <h4 className="font-semibold text-black mb-2">Welche Integrationen sind möglich?</h4>
            <p className="text-gray-600 text-sm">
              Wir integrieren unsere KI-Lösungen in Ihre bestehenden Systeme wie CRM, Warenwirtschaft, 
              Terminkalender und E-Commerce-Plattformen.
            </p>
          </div>
          
          <div className="border-b border-gray-200 pb-4">
            <h4 className="font-semibold text-black mb-2">Wie lange dauert die Implementierung?</h4>
            <p className="text-gray-600 text-sm">
              Je nach Komplexität dauert die Implementierung 2-8 Wochen. Einfache Lösungen wie Terminbuchung 
              können bereits nach wenigen Tagen live gehen.
            </p>
          </div>
          
          <div className="border-b border-gray-200 pb-4">
            <h4 className="font-semibold text-black mb-2">Was kostet eine KI-Lösung?</h4>
            <p className="text-gray-600 text-sm">
              Die Kosten variieren je nach Umfang. Einfache Lösungen starten ab 299€/Monat. 
              In Ihrem kostenlosen Beratungsgespräch erstellen wir ein individuelles Angebot.
            </p>
          </div>
          
          <div className="border-b border-gray-200 pb-4">
            <h4 className="font-semibold text-black mb-2">Ist mein Datenschutz gewährleistet?</h4>
            <p className="text-gray-600 text-sm">
              Ja, alle Lösungen sind DSGVO-konform. Ihre Daten werden ausschließlich in Deutschland verarbeitet 
              und nicht für das Training allgemeiner KI-Modelle verwendet.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-black mb-2">Wie erreiche ich den Support?</h4>
            <p className="text-gray-600 text-sm">
              Sie erreichen uns per E-Mail unter{' '}
              <a href="mailto:mike.trepte@ki-kundenkommunikation.de" className="text-azure-600 hover:underline">
                mike.trepte@ki-kundenkommunikation.de
              </a>
              {' '}oder telefonisch unter 0561 / 43027276.
            </p>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowHelpHub(false)}
            className="px-6 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700"
          >
            Schließen
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
                alt="K³ Logo"
                className="h-10 w-auto mr-4"
              />
              <div>
                <h1 className="text-xl font-bold text-black">Dashboard</h1>
                {onboardingData && (
                  <p className="text-sm text-gray-600">
                    Willkommen, {onboardingData.firstName} {onboardingData.lastName}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowFeedback(true)}
                className="flex items-center text-gray-600 hover:text-azure-600 transition-colors"
              >
                <SafeIcon icon={FiStar} className="text-lg mr-2" />
                <span className="hidden sm:inline">Feedback</span>
              </button>
              
              <button
                onClick={() => setShowHelpHub(true)}
                className="flex items-center text-gray-600 hover:text-azure-600 transition-colors"
              >
                <SafeIcon icon={FiHelpCircle} className="text-lg mr-2" />
                <span className="hidden sm:inline">HelpHub</span>
              </button>
              
              <div className="flex items-center text-gray-600">
                <SafeIcon icon={FiUser} className="text-lg mr-2" />
                <span className="text-sm hidden sm:inline">User ID: {user?.userId?.slice(-8)}</span>
              </div>
              
              <button
                onClick={handleLogout}
                className="flex items-center text-gray-600 hover:text-azure-600 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="text-lg mr-2" />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">
              Willkommen in deinem K³ Dashboard
            </h2>
            <p className="text-xl text-gray-600">
              Hier findest du alle deine KI-Tools und Automatisierungen
            </p>
          </div>

          {/* User Info Card */}
          {onboardingData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-lg mb-8"
            >
              <h3 className="text-xl font-semibold text-black mb-4">Ihr Profil</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Unternehmen:</span>
                  <p className="font-medium">{onboardingData.company}</p>
                </div>
                <div>
                  <span className="text-gray-600">Branche:</span>
                  <p className="font-medium">{onboardingData.businessType}</p>
                </div>
                <div>
                  <span className="text-gray-600">E-Mail:</span>
                  <p className="font-medium">{onboardingData.email}</p>
                </div>
                <div>
                  <span className="text-gray-600">Telefon:</span>
                  <p className="font-medium">{onboardingData.phone}</p>
                </div>
              </div>
              
              {(onboardingData.phoneCallsPerDay || onboardingData.averageCallDuration) && (
                <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
                  {onboardingData.phoneCallsPerDay && (
                    <div>
                      <span className="text-gray-600">Anrufe pro Tag:</span>
                      <p className="font-medium">{onboardingData.phoneCallsPerDay}</p>
                    </div>
                  )}
                  {onboardingData.averageCallDuration && (
                    <div>
                      <span className="text-gray-600">Durchschn. Gesprächsdauer:</span>
                      <p className="font-medium">{onboardingData.averageCallDuration}</p>
                    </div>
                  )}
                </div>
              )}
              
              {onboardingData.interests && onboardingData.interests.length > 0 && (
                <div className="mt-4">
                  <span className="text-gray-600 text-sm">Interessensgebiete:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {onboardingData.interests.map((interest, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-azure-100 text-azure-700 rounded-full text-sm"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiHome} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                Zur Homepage
              </h3>
              <p className="text-gray-600 mb-4">
                Zurück zur K³ Hauptseite
              </p>
              <a
                href="/"
                className="inline-block bg-azure-600 text-white px-4 py-2 rounded-lg hover:bg-azure-700 transition-colors"
              >
                Homepage besuchen
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiCalendar} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                Beratungstermin
              </h3>
              <p className="text-gray-600 mb-4">
                Kostenloses Erstgespräch buchen
              </p>
              <a
                href="https://cal.meetergo.com/mike-trepte/15-min-telefonat-kostenfrei"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-azure-600 text-white px-4 py-2 rounded-lg hover:bg-azure-700 transition-colors"
              >
                Termin buchen
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiSettings} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                KI-Automatisierung
              </h3>
              <p className="text-gray-600 mb-4">
                Deine personalisierten KI-Lösungen
              </p>
              <button className="inline-block bg-gray-200 text-gray-600 px-4 py-2 rounded-lg cursor-not-allowed">
                Bald verfügbar
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiBook} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                Wissensbasis
              </h3>
              <p className="text-gray-600 mb-4">
                Guides und Tutorials zu KI
              </p>
              <button className="inline-block bg-gray-200 text-gray-600 px-4 py-2 rounded-lg cursor-not-allowed">
                Bald verfügbar
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiTrendingUp} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                Analytics
              </h3>
              <p className="text-gray-600 mb-4">
                Performance Ihrer KI-Lösungen
              </p>
              <button className="inline-block bg-gray-200 text-gray-600 px-4 py-2 rounded-lg cursor-not-allowed">
                Bald verfügbar
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow"
            >
              <SafeIcon icon={FiMessageSquare} className="text-3xl text-azure-600 mb-4" />
              <h3 className="text-xl font-semibold text-black mb-2">
                Support Chat
              </h3>
              <p className="text-gray-600 mb-4">
                Direkter Kontakt zum K³ Team
              </p>
              <a
                href="mailto:mike.trepte@ki-kundenkommunikation.de"
                className="inline-block bg-azure-600 text-white px-4 py-2 rounded-lg hover:bg-azure-700 transition-colors"
              >
                Nachricht senden
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 bg-white rounded-xl p-8 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-black mb-4">
              Nächste Schritte
            </h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-azure-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-black">Beratungstermin vereinbaren</h4>
                  <p className="text-gray-600">
                    Lass uns in einem kostenlosen Gespräch deine Anforderungen besprechen
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600">KI-Lösung entwickeln</h4>
                  <p className="text-gray-600">
                    Wir erstellen eine maßgeschneiderte Automatisierung für dein Unternehmen
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-1">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-600">Implementation & Go-Live</h4>
                  <p className="text-gray-600">
                    Deine KI-Automatisierung geht live und revolutioniert deine Kundenkommunikation
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </main>

      {/* Modals */}
      {showFeedback && <FeedbackModal />}
      {showHelpHub && <HelpHubModal />}
    </div>
  );
};

export default DashboardPage;